import UTCDateRange from './UTCDateRange.js';
import DateTimeRange from './DateTimeRange.js';

// A range expression is data that arrives in search criteria and page filters, so it is parsed,
// never evaluated as JavaScript: `Type.Property` or `Type.method(literal, …)`, where a literal is
// a number, a quoted string, true or false. The deprecated getPast and getFuture also take a flat
// options object as their third argument, limited to the options they read.

const UTC_PROPERTIES = [
  'Today',
  'Yesterday',
  'Tomorrow',
  'ThisWeek',
  'LastWeek',
  'NextWeek',
  'ThisMonth',
  'LastMonth',
  'NextMonth',
  'ThisYear',
  'LastYear',
  'NextYear'
];

const METHODS = [
  'this',
  'between',
  'notBetween',
  'range',
  'last',
  'next',
  'earlierThan',
  'laterThan',
  'getRange',
  'getPast',
  'getFuture',
  'getBetween'
];

const METHODS_WITH_OPTIONS = ['getPast', 'getFuture'];
const OPTIONS_POSITION = 2;
const OPTION_KEYS = ['includeToday', 'startOfPeriod', 'endOfPeriod'];

const RANGE_TYPES = {
  UTCDateRange: { type: UTCDateRange, properties: UTC_PROPERTIES, methods: METHODS },
  DateTimeRange: {
    type: DateTimeRange,
    properties: [
      ...UTC_PROPERTIES,
      'ThisMinute',
      'LastMinute',
      'NextMinute',
      'ThisHour',
      'LastHour',
      'NextHour'
    ],
    methods: METHODS
  }
};

const TOKEN =
  /\s*(?:(?<identifier>[A-Za-z_$][\w$]*)|(?<number>-?\d+(?:\.\d+)?)|'(?<single>[^'\\]*)'|"(?<double>[^"\\]*)"|(?<punctuation>[.(),{}:]))/y;

const invalid = value => new Error(`Invalid date range expression: ${String(value)}`);

function tokenize(value) {
  const tokens = [];
  TOKEN.lastIndex = 0;

  while (value.slice(TOKEN.lastIndex).trim()) {
    const match = TOKEN.exec(value);

    if (!match) {
      throw invalid(value);
    }

    const { identifier, number, single, double, punctuation } = match.groups;

    if (identifier !== undefined) tokens.push({ kind: 'identifier', value: identifier });
    else if (number !== undefined) tokens.push({ kind: 'literal', value: Number(number) });
    else if (single !== undefined) tokens.push({ kind: 'literal', value: single });
    else if (double !== undefined) tokens.push({ kind: 'literal', value: double });
    else tokens.push({ kind: 'punctuation', value: punctuation });
  }

  return tokens;
}

/**
 * Reads `[Type.]member` or `[Type.]member(literal, …)` without evaluating anything.
 * `args` is undefined for a property. Throws on anything else.
 */
export function parseDateRangeExpression(value) {
  if (typeof value !== 'string') {
    throw invalid(value);
  }

  const tokens = tokenize(value);
  let position = 0;

  const isNext = text =>
    tokens[position]?.kind === 'punctuation' && tokens[position].value === text;
  const take = (kind, text) => {
    const token = tokens[position++];

    if (!token || token.kind !== kind || (text !== undefined && token.value !== text)) {
      throw invalid(value);
    }

    return token.value;
  };

  const scalar = () => {
    if (
      tokens[position]?.kind === 'identifier' &&
      ['true', 'false'].includes(tokens[position].value)
    ) {
      return take('identifier') === 'true';
    }

    return take('literal');
  };

  const argument = () => {
    if (!isNext('{')) {
      return scalar();
    }

    take('punctuation', '{');
    const options = {};

    while (!isNext('}')) {
      if (Object.keys(options).length) take('punctuation', ',');
      const key = take('identifier');

      if (!OPTION_KEYS.includes(key)) {
        throw invalid(value);
      }

      take('punctuation', ':');
      options[key] = scalar();
    }

    take('punctuation', '}');
    return options;
  };

  let typeName;
  let member = take('identifier');

  if (isNext('.')) {
    take('punctuation', '.');
    typeName = member;
    member = take('identifier');
  }

  let args;

  if (isNext('(')) {
    take('punctuation', '(');
    args = [];

    while (!isNext(')')) {
      if (args.length) take('punctuation', ',');
      args.push(argument());
    }

    take('punctuation', ')');
  }

  if (position !== tokens.length) {
    throw invalid(value);
  }

  return { typeName, member, args };
}

function resolveDateRangeExpression(value, typeNames) {
  const { typeName, member, args } = parseDateRangeExpression(value);
  const rangeType = typeNames.includes(typeName) ? RANGE_TYPES[typeName] : undefined;
  const members = args ? rangeType?.methods : rangeType?.properties;

  if (
    !members?.includes(member) ||
    args?.some((arg, index) => !isAllowedArgument(member, arg, index))
  ) {
    throw invalid(value);
  }

  return { type: rangeType.type, member, args };
}

const isAllowedArgument = (member, arg, index) =>
  typeof arg !== 'object' || (METHODS_WITH_OPTIONS.includes(member) && index === OPTIONS_POSITION);

export function isValidDateRangeExpression(value, typeNames = Object.keys(RANGE_TYPES)) {
  try {
    resolveDateRangeExpression(value, typeNames);
    return true;
  } catch {
    return false;
  }
}

/**
 * Computes the range an expression such as `UTCDateRange.ThisWeek` or
 * `DateTimeRange.last(3, 'day')` stands for. `typeNames` restricts the range types it may name.
 */
export function evaluateDateRangeExpression(value, typeNames = Object.keys(RANGE_TYPES)) {
  const { type, member, args } = resolveDateRangeExpression(value, typeNames);

  return args ? type[member](...args) : type[member];
}
